import { keepPreviousData, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { EVENT_LOG_PAGE_SIZE, type EventLogFacets, type EventLogFilter, type EventLogNewer, type EventLogPage, type EventLogStory } from '@nest-recruit/shared';
import { ApiError, apiGet, apiUrl, errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';

// Admin · Event log (S7; Admin redesign 2026-11-02, Event log A). Read-only — the export is a download, not a change,
// and there is no mutation hook here, and there will not be one.

export const EVENT_LOG_KEY = ['event-log'] as const;
/**
 * Not `/event-log`: common block lists drop any request whose address contains
 * `event-log?`, which left the screen saying "Nothing recorded yet" over 42,000 events.
 */
export const EVENT_LOG_PATH = '/admin/state-changes';
/** "N new · Show" looks again this often, and only while the first page is on screen and the tab is visible. */
const NEWER_POLL_MS = 30_000;

type Query = Readonly<Record<string, string | number | undefined>>;

/** One page of the table. A filter change swaps rows in place, never back to a skeleton (06-qol §5). */
export function useEventLog(filter: EventLogFilter, page: number, isEnabled: boolean): UseQueryResult<EventLogPage, ApiError> {
  return useQuery<EventLogPage, ApiError>({
    queryKey: [...EVENT_LOG_KEY, 'page', filter, page],
    enabled: isEnabled,
    queryFn: () => apiGet<EventLogPage>(EVENT_LOG_PATH, { query: { ...filter, page, pageSize: EVENT_LOG_PAGE_SIZE } satisfies Query }),
    placeholderData: keepPreviousData,
  });
}

export function useEventLogFacets(isEnabled: boolean): UseQueryResult<EventLogFacets, ApiError> {
  return useQuery<EventLogFacets, ApiError>({
    queryKey: [...EVENT_LOG_KEY, 'facets'],
    enabled: isEnabled,
    queryFn: () => apiGet<EventLogFacets>(`${EVENT_LOG_PATH}/facets`),
    staleTime: 60_000,
  });
}

/** One record's story. */
export function useEventLogStory(entityType: string | undefined, entityId: string | undefined, isEnabled: boolean): UseQueryResult<EventLogStory, ApiError> {
  return useQuery<EventLogStory, ApiError>({
    queryKey: [...EVENT_LOG_KEY, 'story', entityType, entityId],
    enabled: isEnabled && entityType !== undefined && entityId !== undefined,
    queryFn: () => apiGet<EventLogStory>(`${EVENT_LOG_PATH}/story`, { query: { entityType, entityId } satisfies Query }),
  });
}

/**
 * "N new · Show": how many rows the same filters have gained since the newest on screen. It asks the server for a
 * number and nothing else — no row arrives until the person clicks Show and the page is fetched (#12's spirit: the
 * screen never changes under someone reading it).
 */
export function useEventLogNewer(filter: EventLogFilter, afterId: string | null, isEnabled: boolean): UseQueryResult<EventLogNewer, ApiError> {
  return useQuery<EventLogNewer, ApiError>({
    queryKey: [...EVENT_LOG_KEY, 'newer', filter, afterId],
    enabled: isEnabled && afterId !== null,
    queryFn: () => apiGet<EventLogNewer>(`${EVENT_LOG_PATH}/newer`, { query: { ...filter, afterId: afterId ?? undefined } satisfies Query }),
    refetchInterval: NEWER_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

/** Show the new rows: the first page is fetched again, and "N new" starts counting from its newest row. */
export function useShowNewer(): () => Promise<void> {
  const client = useQueryClient();
  return useCallback(() => client.invalidateQueries({ queryKey: [...EVENT_LOG_KEY, 'page'] }), [client]);
}

/** `GET /admin/state-changes/export` for what the filters show. */
export function eventLogExportUrl(filter: EventLogFilter): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (typeof value === 'string' && value !== '') params.set(key, value);
  }
  const query = params.toString();
  return apiUrl(`${EVENT_LOG_PATH}/export${query === '' ? '' : `?${query}`}`);
}

function messageOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null || !('error' in payload)) return null;
  const error = payload.error;
  if (typeof error !== 'object' || error === null || !('message' in error)) return null;
  return typeof error.message === 'string' ? error.message : null;
}

/**
 * Fetched rather than opened as a link, so a refusal (422: more than 10,000 events) shows the server's own message
 * instead of a page of raw error text. Resolves once the browser has been handed the file.
 */
export async function downloadEventLogExport(filter: EventLogFilter): Promise<void> {
  let response: Response;
  try {
    response = await fetch(eventLogExportUrl(filter), { credentials: 'same-origin' });
  } catch {
    throw new ApiError({ code: 'network_unreachable', message: "Couldn't reach the server. Check your connection and try again.", status: 0 });
  }
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    throw new ApiError({ code: 'export_failed', message: messageOf(payload) ?? "Couldn't make the file. Try again.", status: response.status });
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = 'event-log.csv';
  link.click();
  URL.revokeObjectURL(url);
}

const TOAST_KEY = 'event-log-export';

/** "Export these rows": the button marks itself while the file is made, and a toast says how it went. A click never waits. */
export function useEventLogExport(): { readonly isPreparing: boolean; readonly run: (filter: EventLogFilter) => void } {
  const [isPreparing, setPreparing] = useState(false);
  const showToast = useToasts((state) => state.show);
  const run = useCallback(
    (filter: EventLogFilter) => {
      if (isPreparing) return;
      setPreparing(true);
      showToast({ key: TOAST_KEY, tone: 'info', message: 'Preparing the file…', detail: 'The rows the filters show. You can keep working.' });
      downloadEventLogExport(filter)
        .then(() => showToast({ key: TOAST_KEY, tone: 'success', message: 'File ready', detail: 'Your browser saves event-log.csv.' }))
        .catch((error: unknown) => showToast({ key: TOAST_KEY, tone: 'danger', message: "The file wasn't made", detail: errorMessage(error) }))
        .finally(() => setPreparing(false));
    },
    [isPreparing, showToast],
  );
  return { isPreparing, run };
}
